import { Download, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Marquee } from "@/components/motion";
import { useOrder } from "@/context/OrderContext";
import { DOCS, waLink } from "@/lib/site";
import { useLang } from "@/context/LangContext";

const BOTTLES = [
  ["bottle-1", "Aglaonema sultan brunei"], ["bottle-2", "Aglaonema lotus delight"], ["bottle-3", "Aglaonema lady valentine"],
  ["bottle-4", "Aglaonema widuri"], ["bottle-5", "Epiprenum amplisimum variegata"],
];
const HAMPERS = [["kelana", "Kelana"], ["aluna", "Aluna"], ["gardenia", "Gardenia"]];

const Bottles = () => {
  const { add } = useOrder();
  const { t } = useLang();
  return (
    <section data-testid="souvenir-tc" className="bg-[var(--cream)] py-20 sm:py-28">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="grid gap-8 lg:grid-cols-2 lg:items-end">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[var(--forest)] leading-[0.95]">Tissue Culture<br />Souvenirs.</h2>
          <p className="text-[var(--muted-ink)] leading-relaxed">{t("Setiap tanaman ditumbuhkan dari potongan kecil jaringan di lingkungan steril dan terkontrol — menghasilkan souvenir yang tahan lama, bebas patogen, dan minim perawatan. Lebih dari sekadar hadiah: wujud keyakinan Minaqu pada pertumbuhan, kehidupan, dan dampak positif.", "Each plant is grown from a small piece of tissue in a sterile, controlled environment — resulting in a long-lasting, pathogen-free and low-maintenance souvenir. More than just a gift: a reflection of Minaqu's belief in growth, life and positive impact.")}</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-3 lg:grid-cols-5">
          {BOTTLES.map(([k, n], i) => (
            <Reveal key={k} delay={i * 0.07} className="group">
              <div className="aspect-[3/4] overflow-hidden rounded-t-[999px] rounded-b-2xl bg-[var(--forest)]">
                <img src={`/assets/img/${k}.jpg`} alt={n} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <p className="mt-3 text-center text-sm italic text-[var(--forest)]"><b className="not-italic font-display">{n.split(" ")[0]}</b> {n.split(" ").slice(1).join(" ")}</p>
              <button data-testid={`souvenir-add-${k}`} onClick={() => add({ id: `souvenir-${k}`, name: `TC Souvenir — ${n}`, img: `/assets/img/${k}.jpg`, price: "" })}
                className="mx-auto mt-2 block rounded-full border border-forest/30 px-4 py-1.5 font-display text-xs font-bold text-[var(--forest)] hover:bg-[var(--forest)] hover:text-white">{t("+ Pesanan", "+ Order")}</button>
            </Reveal>
          ))}
        </div>
        <p className="mt-10 text-xs text-[var(--muted-ink)]">(*) Additional plant varieties not featured in this catalogue are available upon request</p>
      </div>
    </section>
  );
};

const Hampers = () => {
  const { add } = useOrder();
  const { t } = useLang();
  return (
    <section data-testid="souvenir-hampers" className="bg-[var(--forest)] py-20 sm:py-28 text-white grain relative overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="eyebrow !text-[#cfe3c8]">Grow at your own pace</p>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">Pot Plant Hampers Series</h2>
          <p className="mx-auto mt-5 max-w-2xl text-white/85">{t("Dirancang untuk menghadirkan kebahagiaan dan sentuhan personal di setiap momen. Dengan kemasan yang bisa dikustomisasi — elegan, playful, atau minimalis.", "Designed to bring joy and a personal touch to every moment. With customisable packaging — elegant, playful or minimalist.")}</p>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-3">
          {HAMPERS.map(([k, n], i) => (
            <Reveal key={k} delay={i * 0.1} className="group rounded-[2rem] bg-white/5 p-4 ring-1 ring-white/10">
              <div className="aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-2xl">
                <img src={`/assets/img/${k}.jpg`} alt={`Hampers ${n}`} className="h-full w-full scale-[1.12] object-cover transition-transform duration-700 group-hover:scale-[1.18]" />
              </div>
              <div className="mt-5 flex items-center justify-between gap-3 px-2 pb-2">
                <p className="font-display text-2xl font-black tracking-[0.12em]">{n.toUpperCase()}</p>
                <button data-testid={`hampers-add-${k}`} onClick={() => add({ id: `hampers-${k}`, name: `Hampers ${n}`, img: `/assets/img/${k}.jpg`, price: "" })}
                  className="rounded-full bg-white px-4 py-2 font-display text-xs font-bold text-[var(--forest)] hover:bg-[#cfe3c8]">{t("+ Pesanan", "+ Order")}</button>
              </div>
            </Reveal>
          ))}
        </div>
        <ul className="mx-auto mt-14 grid max-w-4xl gap-3 text-sm text-white/90 sm:grid-cols-3">
          <li><b className="font-display">Customable packaging</b> — make it yours, match any vibe!</li>
          <li><b className="font-display">Unique & adorable gift</b> — perfect to make someone smile!</li>
          <li><b className="font-display">Single & bulk orders</b> — great for gift, souvenirs, events, and more!</li>
        </ul>
      </div>
    </section>
  );
};

const Events = () => {
  const { t } = useLang();
  return (
  <section data-testid="souvenir-events" className="bg-[var(--cream)] py-20 sm:py-28">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid gap-4 sm:grid-cols-4 sm:grid-rows-2 sm:auto-rows-[260px]">
        <Reveal className="rounded-[2rem] bg-white p-7 flex flex-col justify-end sm:row-span-1"><p className="font-display text-4xl font-black text-[var(--forest)] leading-none">Wedding<br />Event</p></Reveal>
        {["event-1", "event-2", "event-3"].map((k, i) => (
          <Reveal key={k} delay={i * 0.06} className="overflow-hidden rounded-[2rem] aspect-square sm:aspect-auto"><img src={`/assets/img/${k}.jpg`} alt="Souvenir wedding Minaqu" className="h-full w-full object-cover" /></Reveal>
        ))}
        {["expo-1", "expo-2", "expo-3"].map((k, i) => (
          <Reveal key={k} delay={i * 0.06} className="overflow-hidden rounded-[2rem] aspect-square sm:aspect-auto"><img src={`/assets/img/${k}.jpg`} alt="Trade Expo Indonesia 2025" className="h-full w-full object-cover" /></Reveal>
        ))}
        <Reveal className="rounded-[2rem] bg-[var(--forest)] p-7 flex flex-col justify-end text-white"><p className="font-display text-4xl font-black leading-none">Trade Expo<br />Indonesia<br />2025</p></Reveal>
      </div>
      <Reveal className="mt-14 flex flex-wrap justify-center gap-3">
        <a href={waLink(t("Halo Minaqu Indonesia, saya tertarik dengan souvenir & hampers untuk acara saya.", "Hello Minaqu Indonesia, I am interested in souvenirs & hampers for my event."))} target="_blank" rel="noreferrer" data-testid="souvenir-wa-cta" className="inline-flex items-center gap-2 rounded-full bg-[var(--forest)] px-6 py-3.5 font-display text-sm font-bold text-white"><MessageCircle className="h-4 w-4" /> {t("Konsultasi souvenir", "Souvenir consultation")}</a>
        <a href={DOCS.souvenir} target="_blank" rel="noreferrer" data-testid="souvenir-download-pdf" className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--forest)] px-6 py-3 font-display text-sm font-bold text-[var(--forest)]"><Download className="h-4 w-4" /> {t("Unduh katalog PDF", "Download PDF catalogue")}</a>
      </Reveal>
    </div>
  </section>
  );
};

export default function Souvenir() {
  const { t } = useLang();
  return (
    <>
      <PageHero testid="souvenir-hero" tone="cream" eyebrow="Catalogue · Series of Hampers & Souvenirs 2025" lines={["A little", "green for", "your soul."]} img="/assets/img/souv-cover.jpg" imgAlt={t("Souvenir kultur jaringan Minaqu", "Minaqu tissue culture souvenir")}
        intro={t("Eksklusif untuk hadiah personal, souvenir korporat, giveaway acara, dan banyak lagi — pilihan serbaguna untuk setiap perayaan.", "Exclusive for personal gifts, corporate souvenirs, event giveaways and more — a versatile choice for every celebration.")} />
      <div className="bg-[var(--forest)] py-5 text-[var(--cream)]">
        <Marquee items={["Tissue Culture Souvenir", "Hampers Series", "Kelana · Aluna · Gardenia", "Wedding & Corporate Gifts"]} className="font-display text-xl sm:text-2xl font-extrabold" />
      </div>
      <Bottles />
      <Hampers />
      <Events />
    </>
  );
}
